import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card.tsx';
import { Input } from '../ui/input.tsx';
import { Label } from '../ui/label.tsx';
import { Button } from '../ui/button.tsx';
import apiClient from '../../services/apiClient.ts';

type SetupFormValues = {
  name: string;
  apellidos: string;
  email: string;
  password: string;
};

type SetupFirstAdminProps = {
  onComplete: () => void; // NUEVO
};

export default function SetupFirstAdmin({ onComplete }: SetupFirstAdminProps) {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'checking' | 'ready' | 'error'>('checking');
  const [serverMessage, setServerMessage] = useState<string | null>(null);
  const { register, handleSubmit, formState } = useForm<SetupFormValues>({
    defaultValues: { name: '', apellidos: '', email: '', password: '' },
  });

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const response = await apiClient.get('/system/setup');
        if (!response.data.setup_required) {
            onComplete();
          navigate('/login', { replace: true });
          return;
        }
        setStatus('ready');
      } catch (error) {
        setServerMessage('No pudimos verificar el estado del sistema.');
        setStatus('error');
      }
    };
    fetchStatus();
  }, [navigate, onComplete]);

  const onSubmit = handleSubmit(async (values) => {
    setServerMessage(null);
    try {
      await apiClient.post('/users/create', { ...values, role: 'admin' });
      toast.success('Administrador creado. Ya puedes iniciar sesión.');
        onComplete();
      navigate('/login', { replace: true });
    } catch (error) {
      if (isAxiosError(error)) {
        if (error.response?.status === 409) {
          setServerMessage('El email ya está en uso.');
        } else if (error.response?.status === 422) {
          setServerMessage('Revisa los datos: recuerda mínimo 8 caracteres de contraseña.');
        } else {
          setServerMessage('Error inesperado al registrar el administrador.');
        }
      } else {
        setServerMessage('Error inesperado al registrar el administrador.');
      }
    }
  });

  if (status === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-gray-50">
        <p className="text-red-600 text-sm">{serverMessage}</p>
        <Button onClick={() => window.location.reload()} className="bg-blue-600 hover:bg-blue-700">
          Reintentar
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader>
          <CardTitle>Configura el primer administrador</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={onSubmit}>
            <div>
              <Label htmlFor="name">Nombre</Label>
              <Input id="name" {...register('name', { required: 'Introduce el nombre.' })} autoComplete="given-name" />
              {formState.errors.name && <p className="text-sm text-red-600">{formState.errors.name.message}</p>}
            </div>
            <div>
              <Label htmlFor="apellidos">Apellidos</Label>
              <Input id="apellidos" {...register('apellidos', { required: 'Introduce los apellidos.' })} autoComplete="family-name" />
              {formState.errors.apellidos && <p className="text-sm text-red-600">{formState.errors.apellidos.message}</p>}
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" {...register('email', { required: 'Introduce un email válido.' })} autoComplete="email" />
              {formState.errors.email && <p className="text-sm text-red-600">{formState.errors.email.message}</p>}
            </div>
            <div>
              <Label htmlFor="password">Contraseña</Label>
              <Input id="password" type="password" {...register('password', { required: 'Introduce una contraseña.', minLength: { value: 8, message: 'Mínimo 8 caracteres.' } })} autoComplete="new-password" />
              {formState.errors.password && <p className="text-sm text-red-600">{formState.errors.password.message}</p>}
            </div>
            <input type="hidden" value="admin" readOnly /> {/* Campo virtual para subrayar que el rol será admin. */}
            {serverMessage && <p className="text-sm text-red-600" role="alert">{serverMessage}</p>}
            <Button type="submit" disabled={formState.isSubmitting} className="w-full bg-blue-600 hover:bg-blue-700">
              {formState.isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Crear administrador'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
